import React from 'react'
import { Button, DatePicker, Modal, Switch, Tooltip as AntTooltip, Typography } from 'antd'
import {
  CloseOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import { AxiosError } from 'axios'
import dayjs, { Dayjs } from 'dayjs'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { sellerApi } from 'api/seller'
import { withdrawalRequestsApi } from 'api/withdrawalRequests'
import { orderStatisticsApi } from 'api/orderStatistics'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { ISeller, TVariantSeller } from 'models/seller'
import { IOrderStatisticsDay } from 'models/orderStatistics'
import { TransactionsTable } from 'components/transactions/TransactionsTable/TransactionsTable'
import { MostPurchasedProductsTable } from 'components/goods/MostPurchasedProductsTable/MostPurchasedProductsTable'
import { ChangeBalance } from 'components/sellers/ChangeBalance/ChangeBalance'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { superAdminRoutes } from 'shared/constants/routes'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { getRandomString } from 'shared/utils/getRandomString'
import { SectionTitle } from 'components/ui'
import styles from './SellerAccounting.module.scss'
import { WithdrawalRequestModal } from './ui/WithdrawalRequestModal'
import { getSellerDataError } from './utils/sellerDataErrors'
import 'dayjs/locale/ru'

interface ISellerAccountingProps {
  type: TVariantSeller
  sellerId: string
}

interface IAccountingStatisticsDay extends IOrderStatisticsDay {
  salesVolume: number
}

type TDateRange = [Dayjs, Dayjs]
type TChartType = 'orders' | 'averageCheck' | 'salesVolume'

interface IChartDotProps {
  cx?: number
  cy?: number
  r?: number
  stroke?: string
  strokeWidth?: number
  fill?: string
  payload?: Partial<Record<'averageCheck' | 'salesVolume', number>>
}

const chartTitles: Record<TChartType, string> = {
  orders: 'Заказы по дням',
  averageCheck: 'Средний чек по дням',
  salesVolume: 'Объем продаж'
}

// В подсказке любого графика показываем все три показателя дня,
// первым — тот, чей график открыт
const chartMetrics: {
  key: TChartType
  label: string
  getValue: (day: IAccountingStatisticsDay) => string
}[] = [
  {
    key: 'salesVolume',
    label: 'Объем продаж',
    getValue: (day) => getNormalizedPrice(day.salesVolume)
  },
  {
    key: 'orders',
    label: 'Количество заказов',
    getValue: (day) => String(day.totalOrders)
  },
  {
    key: 'averageCheck',
    label: 'Средний чек',
    getValue: (day) => getNormalizedPrice(day.averageCheck)
  }
]

interface IChartTooltipProps {
  chart: TChartType
  active?: boolean
  label?: string | number
  payload?: ReadonlyArray<{ payload?: IAccountingStatisticsDay }>
}

const ChartTooltip: React.FC<IChartTooltipProps> = ({
  chart,
  active,
  label,
  payload
}) => {
  const day = payload?.[0]?.payload

  if (!active || !day) {
    return null
  }

  const metrics = [
    ...chartMetrics.filter((metric) => metric.key === chart),
    ...chartMetrics.filter((metric) => metric.key !== chart)
  ]

  return (
    <div className={styles.chartTooltip}>
      <p className={styles.chartTooltipDate}>{label}</p>
      {metrics.map((metric) => (
        <p
          key={metric.key}
          className={classNames(styles.chartTooltipRow, {
            [styles.chartTooltipRowActive]: metric.key === chart
          })}
        >
          {metric.label}: {metric.getValue(day)}
        </p>
      ))}
    </div>
  )
}

const AUTO_WITHDRAWAL_HINT =
  'Заявка на вывод подаётся сама, как только деньги за заказ становятся доступны к выплате'
const SELLER_OFFER_URL = 'https://horsesmart.store/seller-offer'

const getDefaultDateRange = (): TDateRange => [
  dayjs().subtract(2, 'week'),
  dayjs()
]

const parseStatisticsDate = (date: string) => {
  const [day, month, year] = date.split('.')
  return dayjs(`${year}-${month}-${day}`)
}

const formatChartPrice = (value: number) => getNormalizedPrice(value)

const isChartDotProps = (props: unknown): props is IChartDotProps => {
  return typeof props === 'object' && props !== null
}

const renderNonZeroDot = (props: unknown) => {
  if (!isChartDotProps(props)) {
    return <></>
  }

  const value = props.payload?.averageCheck ?? props.payload?.salesVolume

  if (!value) {
    return <></>
  }

  const { cx, cy, r, stroke, strokeWidth, fill } = props

  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      stroke={stroke}
      strokeWidth={strokeWidth}
      fill={fill}
    />
  )
}

export const SellerAccounting: React.FC<ISellerAccountingProps> = ({
  type,
  sellerId
}) => {
  const navigate = useNavigate()

  const [orderStatisticsDays, setOrderStatisticsDays] = React.useState<IAccountingStatisticsDay[]>([])
  const [seller, setSeller] = React.useState<ISeller | null>(null)
  const [dateRange, setDateRange] = React.useState<TDateRange>(getDefaultDateRange)
  const [isChartsLoading, setIsChartsLoading] = React.useState(false)
  const [openedChart, setOpenedChart] = React.useState<TChartType | null>(null)

  const [isOpenWithdrawalRequestModal, setIsOpenWithdrawalRequestModal] = React.useState(false)
  const [isRecalculating, setIsRecalculating] = React.useState(false)
  const [isAutoWithdrawalSaving, setIsAutoWithdrawalSaving] = React.useState(false)

  const [isNeedToUpdateTransactions, setIsNeedToUpdateTransactions] =
    React.useState('')

  React.useEffect(() => {
    getSeller()
  }, [])

  React.useEffect(() => {
    if (seller) {
      getChartsData()
    }
  }, [seller, dateRange])

  const getSeller = async () => {
    try {
      const response = await sellerApi.getSeller(sellerId)
      setSeller(response.data)
    } catch (e) {
      const error = e as AxiosError
      if (
        (error.response?.status === 500 ||
          error.response?.status === 400 ||
          error.response?.status === 404) &&
        type === 'seller'
      ) {
        return navigate(superAdminRoutes.SELLERS)
      }
      showServerError(e)
    }
  }

  const getChartsData = async () => {
    try {
      setIsChartsLoading(true)
      const [from, to] = dateRange
      const orderStatisticsResponse = await orderStatisticsApi.getOrderStatistics({
        from: from.format('YYYY-MM-DD'),
        to: to.format('YYYY-MM-DD'),
        seller_id: sellerId
      })

      setOrderStatisticsDays(
        orderStatisticsResponse.data.days
          .filter((item) =>
            !parseStatisticsDate(item.date).isAfter(dayjs(), 'day')
          )
          .map((item) => ({
            ...item,
            salesVolume: item.totalOrders * item.averageCheck
          }))
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setIsChartsLoading(false)
    }
  }

  const updatePageData = () => {
    getSeller()
    setIsNeedToUpdateTransactions(getRandomString())
  }

  const recalculateBalance = async () => {
    try {
      setIsRecalculating(true)
      const response = await sellerApi.recalculateBalance(sellerId)
      setSeller(response.data)
      showMessage('success', 'Данные обновлены', 'Баланс продавца пересчитан')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsRecalculating(false)
    }
  }

  const onSuccessCreateTransaction = () => {
    updatePageData()
  }

  const openWithdrawalRequestModal = () => {
    setIsOpenWithdrawalRequestModal(true)
    bodyScrollLock.enable()
  }

  const onCloseWithdrawalRequestModal = React.useCallback(() => {
    setIsOpenWithdrawalRequestModal(false)
    bodyScrollLock.disable()
  }, [])

  const onSuccessWithdrawalRequestModal = React.useCallback(() => {
    updatePageData()
  }, [])

  const saveAutoWithdrawal = async (enabled: boolean) => {
    try {
      setIsAutoWithdrawalSaving(true)
      const { data } = await withdrawalRequestsApi.setAutoWithdrawal({
        seller_id: sellerId,
        enabled
      })
      setSeller((prev) => prev && { ...prev, auto_withdrawal: data.auto_withdrawal })
      if (data.submitted_count) {
        updatePageData()
      }
      showMessage(
        'success',
        enabled ? 'Автоматические выплаты включены' : 'Автоматические выплаты отключены',
        data.submitted_count ? `Продаж в заявке на вывод: ${data.submitted_count}` : ''
      )
    } catch (e) {
      const sellerDataError = getSellerDataError(e)
      if (!sellerDataError) {
        return showServerError(e)
      }
      showMessage('error', sellerDataError.message, '')
      // Ссылки ведут в собственный профиль — супер-админу туда незачем
      if (type === 'profile') {
        navigate(sellerDataError.route)
      }
    } finally {
      setIsAutoWithdrawalSaving(false)
    }
  }

  const onAutoWithdrawalChange = (checked: boolean) => {
    if (!checked) {
      saveAutoWithdrawal(false)
      return
    }

    Modal.confirm({
      centered: true,
      icon: <ExclamationCircleOutlined />,
      title: 'Включить автоматические выплаты?',
      content: (
        <>
          <p>
            В автоматическом режиме заявка на вывод подаётся, как только выплата
            становится доступна, — подавать её вручную не нужно.
          </p>
          <p>
            Выплаты происходят дважды в месяц, согласно{' '}
            <a href={SELLER_OFFER_URL} target='_blank' rel='noreferrer'>оферте</a>, по
            расчётным периодам: с 26-го по 10-е и с 11-го по 25-е число. Всё, что
            доступно к выводу за период, будет выплачено в ближайший срок.
          </p>
          <p>
            Без автоматического режима, если заявка не была подана вовремя, то
            провести выплату будет возможно только в конце следующего периода.
          </p>
          <p style={{ marginBottom: 0 }}>
            Заказы, доступные к выводу на текущий момент, попадут в заявку сразу
            после включения.
            <br />
            Хотите включить автоматические выплаты?
          </p>
        </>
      ),
      okText: 'Включить',
      cancelText: 'Отмена',
      onOk: () => saveAutoWithdrawal(true)
    })
  }

  const onDateRangeChange = (value: null | [Dayjs | null, Dayjs | null]) => {
    if (!value?.[0] || !value?.[1]) {
      return
    }

    setDateRange([value[0], value[1]])
  }

  const openChart = (chart: TChartType) => {
    setOpenedChart(chart)
  }

  const closeChart = () => {
    setOpenedChart(null)
  }

  const renderOrdersChart = () => (
    <ResponsiveContainer width='100%' height='100%'>
      <BarChart data={orderStatisticsDays} margin={{ top: 8, right: 10, bottom: 8, left: 0 }}>
        <CartesianGrid stroke='#dfe8f5' vertical={false} />
        <XAxis
          dataKey='date'
          interval='preserveStartEnd'
          minTickGap={28}
          tick={{ fill: '#637aa0', fontSize: 12 }}
        />
        <YAxis
          allowDecimals={false}
          width={42}
          tick={{ fill: '#637aa0', fontSize: 12 }}
        />
        <Tooltip content={(props) => <ChartTooltip {...props} chart='orders' />} />
        <Bar
          dataKey='totalOrders'
          name='Заказы'
          fill='#4f86e8'
          radius={[7, 7, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const renderAverageCheckChart = () => (
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={orderStatisticsDays} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
        <CartesianGrid stroke='#dfe8f5' vertical={false} />
        <XAxis
          dataKey='date'
          interval='preserveStartEnd'
          minTickGap={28}
          tick={{ fill: '#637aa0', fontSize: 12 }}
        />
        <YAxis
          width={70}
          tick={{ fill: '#637aa0', fontSize: 12 }}
          tickFormatter={formatChartPrice}
        />
        <Tooltip content={(props) => <ChartTooltip {...props} chart='averageCheck' />} />
        <Line
          dataKey='averageCheck'
          name='Средний чек'
          type='monotone'
          stroke='#183554'
          strokeWidth={3}
          dot={renderNonZeroDot}
          activeDot={renderNonZeroDot}
        />
      </LineChart>
    </ResponsiveContainer>
  )

  const renderSalesVolumeChart = () => (
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={orderStatisticsDays} margin={{ top: 8, right: 10, bottom: 8, left: 0 }}>
        <CartesianGrid stroke='#dfe8f5' vertical={false} />
        <XAxis
          dataKey='date'
          interval='preserveStartEnd'
          minTickGap={40}
          tick={{ fill: '#637aa0', fontSize: 12 }}
        />
        <YAxis
          width={76}
          tick={{ fill: '#637aa0', fontSize: 12 }}
          tickFormatter={formatChartPrice}
        />
        <Tooltip content={(props) => <ChartTooltip {...props} chart='salesVolume' />} />
        <Line
          dataKey='salesVolume'
          name='Объем продаж'
          type='monotone'
          stroke='#2f75ff'
          strokeWidth={3}
          dot={renderNonZeroDot}
          activeDot={renderNonZeroDot}
        />
      </LineChart>
    </ResponsiveContainer>
  )

  const renderChart = (chart: TChartType) => {
    if (chart === 'orders') {
      return renderOrdersChart()
    }

    if (chart === 'averageCheck') {
      return renderAverageCheckChart()
    }

    return renderSalesVolumeChart()
  }

  if (!seller) {
    return null
  }

  return (
    <>
      <div style={{ paddingBottom: 80 }}>
        <div className={styles.statisticsHeader}>
          <SectionTitle level={2} style={{ margin: 0 }}>
            Бухгалтерия
          </SectionTitle>
          <DatePicker.RangePicker
            value={dateRange}
            format='DD.MM.YYYY'
            onChange={onDateRangeChange}
            allowClear={false}
            inputReadOnly
          />
        </div>
        <div className={styles.chartsGrid}>
          <div
            className={styles.chartCard}
            role='button'
            tabIndex={0}
            onClick={() => openChart('orders')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                openChart('orders')
              }
            }}
          >
            <Typography.Title level={4} className={styles.chartTitle}>
              {chartTitles.orders}
            </Typography.Title>
            <div className={styles.chartBody}>
              {renderOrdersChart()}
            </div>
          </div>
          <div
            className={styles.chartCard}
            role='button'
            tabIndex={0}
            onClick={() => openChart('averageCheck')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                openChart('averageCheck')
              }
            }}
          >
            <Typography.Title level={4} className={styles.chartTitle}>
              {chartTitles.averageCheck}
            </Typography.Title>
            <div className={styles.chartBody}>
              {renderAverageCheckChart()}
            </div>
          </div>
          <div
            className={classNames(styles.chartCard, styles.chartCardWide)}
            role='button'
            tabIndex={0}
            onClick={() => openChart('salesVolume')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                openChart('salesVolume')
              }
            }}
          >
            <Typography.Title level={4} className={styles.chartTitle}>
              {chartTitles.salesVolume}
            </Typography.Title>
            <div className={classNames(styles.chartBody, styles.chartBodyWide)}>
              {renderSalesVolumeChart()}
            </div>
          </div>
        </div>
        <div className={styles.moneyInfo}>
          <div className={styles.moneyInfoItem}>
            <Typography.Title level={3} style={{ marginBottom: 3 }}>
              {getNormalizedPrice(seller.balance_info.total_turnover)}
            </Typography.Title>
            <div className={classNames({ [styles.conclusionWrapper]: type === 'seller' })}>
              <Typography.Text>Общий оборот</Typography.Text>
              {type === 'seller' && (
                <Button
                  htmlType='button'
                  type='primary'
                  size='middle'
                  loading={isRecalculating}
                  onClick={recalculateBalance}
                  className={styles.conclusionButton}
                  style={{ marginTop: 11 }}
                >
                  Пересчитать
                </Button>
              )}
            </div>
          </div>
          <div className={styles.moneyInfoItem}>
            <Typography.Title level={3} style={{ marginBottom: 3 }}>
              {getNormalizedPrice(seller.balance_info.withdrawn)}
            </Typography.Title>
            <div
              className={classNames({
                [styles.conclusionWrapper]: type === 'profile' || type === 'seller'
              })}
            >
              <Typography.Text>Выведено</Typography.Text>
              {(type === 'profile' || type === 'seller') && (
                <div className={styles.autoWithdrawal}>
                  <Switch
                    checked={Boolean(seller.auto_withdrawal)}
                    loading={isAutoWithdrawalSaving}
                    onChange={onAutoWithdrawalChange}
                  />
                  <span>Автоматические выплаты</span>
                  <AntTooltip title={AUTO_WITHDRAWAL_HINT}>
                    <QuestionCircleOutlined className={styles.hintIcon} />
                  </AntTooltip>
                </div>
              )}
            </div>
          </div>
          <div style={{ border: 'none' }} className={styles.moneyInfoItem}>
            <Typography.Title level={3} style={{ marginBottom: 3 }}>
              {getNormalizedPrice(
                (seller.balance_info.balance ?? 0) +
                (seller.balance_info.withdrawal_requested ?? 0) +
                (seller.balance_info.pending_withdrawal ?? 0) +
                (seller.balance_info.retained_balance ?? 0)
              )}
            </Typography.Title>
            <div
              className={classNames({
                [styles.conclusionWrapper]: type === 'profile' || type === 'seller'
              })}
            >
              <Typography.Text>Текущий баланс</Typography.Text>
              {(type === 'profile' || type === 'seller') && (
                <Button
                  htmlType='button'
                  type='primary'
                  size='middle'
                  onClick={openWithdrawalRequestModal}
                  className={styles.conclusionButton}
                  style={{ marginTop: 11 }}
                >
                  Заявка на вывод
                </Button>
              )}
            </div>
          </div>
        </div>
        {type === 'seller' && (
          <ChangeBalance
            sellerId={sellerId}
            style={{ marginBottom: 24 }}
            onSuccess={onSuccessCreateTransaction}
          />
        )}
        <TransactionsTable
          sellerId={sellerId}
          isNeedToUpdate={isNeedToUpdateTransactions}
          showWithdrawalModal={type === 'profile' || type === 'seller'}
          useAdminWithdrawalApi={type === 'seller'}
          style={{ marginBottom: 17 }}
        />
        <MostPurchasedProductsTable sellerId={sellerId} />
      </div>
      <Modal
        centered
        open={Boolean(openedChart)}
        footer={null}
        closable={false}
        onCancel={closeChart}
        width={1100}
        className={styles.chartModal}
      >
        <div className={styles.chartModalHeader}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {openedChart ? chartTitles[openedChart] : ''}
          </Typography.Title>
          <CloseOutlined onClick={closeChart} className={styles.chartModalClose} />
        </div>
        <div className={styles.chartModalBody}>
          {openedChart && renderChart(openedChart)}
        </div>
      </Modal>
      {(type === 'profile' || type === 'seller') && (
        <WithdrawalRequestModal
          sellerId={sellerId}
          availableBalance={seller.balance_info.balance}
          withdrawalRequested={seller.balance_info.withdrawal_requested ?? 0}
          pendingWithdrawal={seller.balance_info.pending_withdrawal ?? 0}
          retainedBalance={seller.balance_info.retained_balance ?? 0}
          autoWithdrawal={Boolean(seller.auto_withdrawal)}
          isOpen={isOpenWithdrawalRequestModal}
          close={onCloseWithdrawalRequestModal}
          onSuccess={onSuccessWithdrawalRequestModal}
        />
      )}
    </>
  )
}
